import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { Bell, ChevronRight, Menu, Moon, Search, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuthStore } from "@/store/authStore";
import { useNotificationStore } from "@/store/notificationStore";
import { useThemeStore } from "@/store/themeStore";
import { cn } from "@/lib/utils";
import UserMenu from "./UserMenu";
import BrandMark from "./BrandMark";
import { activeNavItem, rolePaths, type SidebarItem } from "./nav";

interface TopAppBarProps {
  title: string;
  /** Sidebar items, used to name the current page in the breadcrumb. */
  items?: SidebarItem[];
  /** Opens the mobile navigation drawer. */
  onMenuToggle?: () => void;
  onOpenSearch?: () => void;
  isMobile?: boolean;
}

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

const roundAction =
  "relative size-10 rounded-full border border-border bg-card text-body shadow-[0_1px_2px_rgb(0_0_0/0.03)] hover:border-hairline-strong hover:bg-accent hover:text-foreground";

function Crumbs({ title, items }: { title: string; items: SidebarItem[] }) {
  const { pathname } = useLocation();
  const current = activeNavItem(items, pathname);
  const home = items[0];
  const isDeeper = current && pathname.replace(/\/$/, "") !== current.path.replace(/\/$/, "");

  const crumbs: { label: string; to?: string }[] = [
    { label: title, to: home && home.path !== current?.path ? home.path : undefined },
  ];
  if (current) crumbs.push({ label: current.label, to: isDeeper ? current.path : undefined });
  if (isDeeper && current) {
    const rest = pathname.slice(current.path.replace(/\/$/, "").length).split("/").filter(Boolean);
    // Word segments ("new", "assign") name the page; anything else is a record id.
    const word = /^[a-z][a-z-]*$/.test(rest[0] ?? "") ? rest[0] : null;
    crumbs.push({ label: word ? word.charAt(0).toUpperCase() + word.slice(1).replace(/-/g, " ") : "Details" });
  }

  return (
    <nav aria-label="Breadcrumb" className="min-w-0">
      <ol className="flex min-w-0 items-center gap-1.5 text-sm text-muted-foreground">
        <li className="hidden shrink-0 items-center gap-1.5 lg:flex">
          <span>Taskpart</span>
          <ChevronRight className="size-3.5 opacity-60" aria-hidden />
        </li>
        {crumbs.map((crumb, i) => {
          const last = i === crumbs.length - 1;
          return (
            <li key={`${crumb.label}-${i}`} className={cn("flex min-w-0 items-center gap-1.5", !last && "shrink-0")}>
              {i > 0 && <ChevronRight className="size-3.5 shrink-0 opacity-60" aria-hidden />}
              {crumb.to && !last ? (
                <Link
                  to={crumb.to as any}
                  className="truncate rounded-sm transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {crumb.label}
                </Link>
              ) : (
                <span
                  aria-current={last ? "page" : undefined}
                  className={cn("truncate", last && "font-medium text-foreground")}
                >
                  {crumb.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export default function TopAppBar({ title, items = [], onMenuToggle, onOpenSearch, isMobile }: TopAppBarProps) {
  const user = useAuthStore((s) => s.user);
  const unreadCount = useNotificationStore((s) => s.unreadCount);
  const mode = useThemeStore((s) => s.mode);
  const toggleTheme = useThemeStore((s) => s.toggleTheme);
  const navigate = useNavigate();
  const paths = rolePaths(user?.role);

  const actions = (
    <div className="ml-auto flex shrink-0 items-center gap-1.5 md:gap-2">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="icon" onClick={onOpenSearch} aria-label="Search" className={roundAction}>
            <Search />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Search · {isMac ? "⌘" : "Ctrl"} K</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            aria-label={mode === "dark" ? "Switch to light theme" : "Switch to dark theme"}
            className={cn(roundAction, "max-sm:hidden")}
          >
            {mode === "dark" ? <Sun /> : <Moon />}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{mode === "dark" ? "Light theme" : "Dark theme"}</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate({ to: paths.notifications as any })}
            aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : "Notifications"}
            className={roundAction}
          >
            <Bell />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] leading-none font-medium text-primary-foreground ring-2 ring-background">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>Notifications</TooltipContent>
      </Tooltip>
      <div className="md:hidden">
        <UserMenu variant="topbar" />
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center gap-2 border-b border-border bg-background/90 px-3 backdrop-blur-md">
        <Button variant="ghost" size="icon" onClick={onMenuToggle} aria-label="Open navigation" className={roundAction}>
          <Menu />
        </Button>
        <div className="flex min-w-0 items-center gap-2">
          <BrandMark showWordmark={false} size="sm" />
          <p className="truncate text-sm font-medium text-foreground">{title}</p>
        </div>
        {actions}
      </header>
    );
  }

  return (
    <header className="shrink-0 px-4 pt-4 md:px-5">
      <div className="flex h-16 items-center gap-3 rounded-2xl border border-border bg-card px-5 shadow-[0_1px_2px_rgb(0_0_0/0.03)]">
        <Crumbs title={title} items={items} />
        {actions}
      </div>
    </header>
  );
}
